import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import type { User } from "../types/user";
import type { DomainRecord, HostingInstance, MailboxRecord } from "../types/services";
import { useAdmin } from "../hooks/useAdmin";
import { 
  User as UserIcon, 
  Mail, 
  Calendar, 
  Shield, 
  CreditCard, 
  Sparkles, 
  CheckCircle2, 
  AlertTriangle, 
  RefreshCw, 
  Search, 
  ShieldAlert, 
  CheckCircle,
  HelpCircle,
  Cpu,
  Layers,
  HardDrive,
  Globe,
  Server,
  Plus,
  Trash2,
  ExternalLink,
  ChevronRight,
  MessageSquare
} from "lucide-react";

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  
  // General Page Navigation Tab
  const [activeTab, setActiveTab] = useState<"overview" | "domain" | "hosting" | "email" | "admin">("overview");
  const [adminSubTab, setAdminSubTab] = useState<"users" | "messages">("users");

  const isSuperadmin = user?.role === "superadmin";

  // Admin-specific hook
  const {
    users,
    filteredUsers,
    isLoadingUsers,
    searchQuery,
    setSearchQuery,
    adminError,
    updatingUserId,
    fetchUsers,
    handleUpdateSubscription,
    messages,
    isLoadingMessages,
    fetchMessages,
  } = useAdmin(token, isSuperadmin);

  // --- Premium Cloud Services Local States (Persisted in localStorage by User Email) ---
  const [domains, setDomains] = useState<DomainRecord[]>([]);
  const [instances, setInstances] = useState<HostingInstance[]>([]);
  const [mailboxes, setMailboxes] = useState<MailboxRecord[]>([]);

  // Domain search mock states
  const [domainQuery, setDomainQuery] = useState("");
  const [domainTld, setDomainTld] = useState(".com");
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [domainStatus, setDomainStatus] = useState<"idle" | "available" | "taken">("idle");

  // Hosting instance deploy mock states
  const [instanceName, setInstanceName] = useState("");
  const [cpuCores, setCpuCores] = useState(2);
  const [ramSize, setRamSize] = useState(4);
  const [ssdSize, setSsdSize] = useState(50);

  // Email hosting mock states
  const [emailPrefix, setEmailPrefix] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [emailPassword, setEmailPassword] = useState("");

  // Load persisted states from localStorage
  useEffect(() => {
    if (user) {
      const emailKey = `wesi_services_${user.email}`;
      const savedData = localStorage.getItem(emailKey);
      
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setDomains(parsed.domains || []);
          setInstances(parsed.instances || []);
          setMailboxes(parsed.mailboxes || []);
        } catch (e) {
          console.error("Failed to parse localStorage", e);
        }
      } else {
        // Initialize default mock data for new users
        const defaultDomains: DomainRecord[] = [
          { name: "wesi-cloud-app.com", status: "Active", created_at: new Date().toISOString() }
        ];
        const defaultInstances: HostingInstance[] = [
          { name: "wesi-vm-primary", ip: "103.54.21.90", specs: "2 vCPU / 4GB RAM / 50GB SSD", status: "Active", created_at: new Date().toISOString(), progress: 100 }
        ];
        const defaultMailboxes: MailboxRecord[] = [
          { email: "support@wesi-cloud-app.com", created_at: new Date().toISOString() }
        ];

        setDomains(defaultDomains);
        setInstances(defaultInstances);
        setMailboxes(defaultMailboxes);

        localStorage.setItem(emailKey, JSON.stringify({
          domains: defaultDomains,
          instances: defaultInstances,
          mailboxes: defaultMailboxes
        }));
      }
    }
  }, [user]);

  // Save changes to localStorage helper
  const saveToLocalStorage = (newDomains: DomainRecord[], newInstances: HostingInstance[], newMailboxes: MailboxRecord[]) => {
    if (user) {
      const emailKey = `wesi_services_${user.email}`;
      localStorage.setItem(emailKey, JSON.stringify({
        domains: newDomains,
        instances: newInstances,
        mailboxes: newMailboxes
      }));
    }
  };

  // Poll progress for provisioning VMs
  useEffect(() => {
    const provisioning = instances.some(inst => inst.status === "Provisioning");
    if (provisioning && user) {
      const interval = setInterval(() => {
        const emailKey = `wesi_services_${user.email}`;
        const updated = instances.map(inst => {
          if (inst.status === "Provisioning") {
            const nextProgress = inst.progress + 25;
            if (nextProgress >= 100) {
              // Generate a mock random public IP
              const octet1 = 100 + Math.floor(Math.random() * 50);
              const octet2 = Math.floor(Math.random() * 254);
              const octet3 = Math.floor(Math.random() * 254);
              const octet4 = 10 + Math.floor(Math.random() * 200);
              return {
                ...inst,
                progress: 100,
                status: "Active" as const,
                ip: `${octet1}.${octet2}.${octet3}.${octet4}`
              };
            }
            return { ...inst, progress: nextProgress };
          }
          return inst;
        });

        setInstances(updated);
        saveToLocalStorage(domains, updated, mailboxes);
      }, 1200);

      return () => clearInterval(interval);
    }
  }, [instances, domains, mailboxes, user]);

  // Set default selected domain if list changes
  useEffect(() => {
    if (domains.length > 0 && !selectedDomain) {
      const activeDomains = domains.filter(d => d.status === "Active");
      if (activeDomains.length > 0 && activeDomains[0]) {
        setSelectedDomain(activeDomains[0].name);
      }
    }
  }, [domains, selectedDomain]);

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center font-sans">
        <div className="text-center">
          <RefreshCw className="animate-spin text-brand-purple mx-auto mb-4" size={32} />
          <p className="text-slate-400">Loading your profile workspace...</p>
        </div>
      </div>
    );
  }

  const formattedJoinedDate = new Date(user.created_at).toLocaleDateString("en-MY", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const isUserSubscribed = user.subscription_status === "active";

  // --- Domain Actions ---
  const handleCheckDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainQuery.trim()) return;
    
    setIsCheckingDomain(true);
    setDomainStatus("idle");

    setTimeout(() => {
      setIsCheckingDomain(false);
      const cleanName = domainQuery.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
      const fullDomain = `${cleanName}${domainTld}`;
      
      const exists = domains.some(d => d.name === fullDomain);
      if (exists || cleanName === "google" || cleanName === "apple" || cleanName === "wesi") {
        setDomainStatus("taken");
      } else {
        setDomainStatus("available");
      }
    }, 1000);
  };

  const handleRegisterDomain = () => {
    const cleanName = domainQuery.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    const fullDomain = `${cleanName}${domainTld}`;

    const newRecord: DomainRecord = {
      name: fullDomain,
      status: "Pending",
      created_at: new Date().toISOString()
    };

    const nextDomains = [...domains, newRecord];
    setDomains(nextDomains);
    saveToLocalStorage(nextDomains, instances, mailboxes);
    setDomainQuery("");
    setDomainStatus("idle");

    // Automatically activate the domain after 4 seconds
    setTimeout(() => {
      setDomains(prev => {
        const updated = prev.map(d => d.name === fullDomain ? { ...d, status: "Active" as const } : d);
        saveToLocalStorage(updated, instances, mailboxes);
        return updated;
      });
    }, 4000);
  };

  const handleDeleteDomain = (domainName: string) => {
    const nextDomains = domains.filter(d => d.name !== domainName);
    const nextMailboxes = mailboxes.filter(m => !m.email.endsWith(`@${domainName}`));
    setDomains(nextDomains);
    setMailboxes(nextMailboxes);
    saveToLocalStorage(nextDomains, instances, nextMailboxes);
  };

  // --- Hosting Actions ---
  const handleDeployInstance = (e: React.FormEvent) => {
    e.preventDefault();
    if (!instanceName.trim()) return;

    const cleanInstanceName = instanceName.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    const specsStr = `${cpuCores} vCPU / ${ramSize}GB RAM / ${ssdSize}GB SSD`;

    const newInstance: HostingInstance = {
      name: cleanInstanceName,
      ip: "Pending Allocation",
      specs: specsStr,
      status: "Provisioning",
      created_at: new Date().toISOString(),
      progress: 0
    };

    const nextInstances = [...instances, newInstance];
    setInstances(nextInstances);
    saveToLocalStorage(domains, nextInstances, mailboxes);
    setInstanceName("");
  };

  const handleDeleteInstance = (name: string) => {
    const nextInstances = instances.filter(i => i.name !== name);
    setInstances(nextInstances);
    saveToLocalStorage(domains, nextInstances, mailboxes);
  };

  // --- Mailbox Actions ---
  const handleCreateMailbox = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailPrefix.trim() || !selectedDomain) return;

    const cleanPrefix = emailPrefix.trim().toLowerCase().replace(/[^a-z0-9._-]/g, "");
    const fullEmail = `${cleanPrefix}@${selectedDomain}`;

    const exists = mailboxes.some(m => m.email === fullEmail);
    if (exists) {
      alert("Mailbox address already exists!");
      return;
    }

    const newMailbox: MailboxRecord = {
      email: fullEmail,
      created_at: new Date().toISOString()
    };

    const nextMailboxes = [...mailboxes, newMailbox];
    setMailboxes(nextMailboxes);
    saveToLocalStorage(domains, instances, nextMailboxes);
    setEmailPrefix("");
    setEmailPassword("");
  };

  const handleDeleteMailbox = (emailAddress: string) => {
    const nextMailboxes = mailboxes.filter(m => m.email !== emailAddress);
    setMailboxes(nextMailboxes);
    saveToLocalStorage(domains, instances, nextMailboxes);
  };



  return (
    <div className="w-full max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 font-sans text-white">
      {/* Top Welcome Title */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div>
          <span className="text-xs font-mono font-bold tracking-widest text-brand-electric uppercase bg-brand-purple/10 border border-brand-purple/20 px-3 py-1 rounded-full">
            User Center Dashboard
          </span>
          <h1 className="font-orbitron text-3xl md:text-4xl font-black tracking-wider mt-3 bg-linear-to-r from-white via-slate-100 to-slate-400 bg-clip-text text-transparent">
            WELCOME BACK, {user.name.toUpperCase()}
          </h1>
          <p className="text-sm text-slate-400 mt-2 font-mono">
            Secure Cloud ID: WESI-{user.id.toString().padStart(5, "0")}
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex flex-wrap bg-slate-950/80 border border-white/10 p-1.5 rounded-xl self-start shrink-0 font-mono text-xs font-bold gap-1">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer ${
              activeTab === "overview"
                ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Overview
          </button>
          
          {/* Premium tabs - Only show if Premium / Active */}
          {isUserSubscribed && !isSuperadmin && (
            <>
              <button
                onClick={() => setActiveTab("domain")}
                className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === "domain"
                    ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Globe size={13} />
                <span>Domain Manager</span>
              </button>
              <button
                onClick={() => setActiveTab("hosting")}
                className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === "hosting"
                    ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Server size={13} />
                <span>Cloud Hosting</span>
              </button>
              <button
                onClick={() => setActiveTab("email")}
                className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === "email"
                    ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Mail size={13} />
                <span>Email Hosting</span>
              </button>
            </>
          )}

          {/* Superadmin Tabs */}
          {isSuperadmin && (
            <>
              <button
                onClick={() => {
                  setActiveTab("admin");
                  setAdminSubTab("users");
                }}
                className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === "admin" && adminSubTab === "users"
                    ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Shield size={13} />
                <span>User Registry</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab("admin");
                  setAdminSubTab("messages");
                }}
                className={`px-4.5 py-2 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === "admin" && adminSubTab === "messages"
                    ? "bg-brand-purple text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <MessageSquare size={13} />
                <span>NGL ! Messages</span>
              </button>
            </>
          )}
        </div>
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
          
          {/* Profile Card Info */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col justify-between">
            <div className="absolute -top-10 -left-10 -z-10 h-24 w-24 rounded-full bg-violet-600/10 blur-xl" />
            
            <div>
              <div className="flex items-center gap-4.5 mb-8">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name}
                    className="w-16 h-16 rounded-2xl border border-brand-purple/40 shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-brand-purple/20 border-2 border-brand-purple/50 text-brand-electric flex items-center justify-center font-orbitron font-black text-2xl uppercase shadow-md">
                    {user.name.substring(0, 2)}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="font-bold text-lg text-white">{user.name}</span>
                  <span className="text-xs text-brand-electric font-mono capitalize">{user.role} Portal</span>
                </div>
              </div>

              <div className="space-y-4 font-mono text-xs text-slate-300">
                <div className="flex items-center gap-3 bg-white/5 border border-white/5 rounded-xl p-3">
                  <Mail size={16} className="text-slate-400 shrink-0" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-[10px] text-slate-500 uppercase">Email</span>
                    <span className="truncate text-white font-medium">{user.email}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-white/5 border border-white/5 rounded-xl p-3">
                  <Calendar size={16} className="text-slate-400 shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase">Joined On</span>
                    <span className="text-white font-medium">{formattedJoinedDate}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-white/5 border border-white/5 rounded-xl p-3">
                  <Shield size={16} className="text-slate-400 shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase">Account Role</span>
                    <span className="text-white font-medium capitalize">{user.role}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <button
                onClick={logout}
                className="w-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 font-mono text-xs font-bold rounded-xl py-3 tracking-widest uppercase transition-all active:scale-95 cursor-pointer"
              >
                Terminate Session
              </button>
            </div>
          </div>

          {/* Subscription Status Card */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col justify-between">
            <div className="absolute -bottom-12 -right-12 -z-10 h-32 w-32 rounded-full bg-indigo-600/10 blur-2xl" />

            <div>
              <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-6">
                <span className="font-orbitron font-bold tracking-widest text-slate-400 text-sm">
                  SUBSCRIPTION MODULE
                </span>
                <div className="flex items-center gap-2">
                  <CreditCard size={14} className="text-slate-400" />
                  <span className="text-xs font-mono text-slate-400 uppercase">Manual Billing</span>
                </div>
              </div>

              {isSuperadmin ? (
                // Superadmin Subscription view
                <div className="space-y-6">
                  <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-5 flex gap-4">
                    <Sparkles className="text-brand-electric shrink-0 animate-pulse mt-0.5" size={24} />
                    <div>
                      <h3 className="font-bold text-white text-lg">System Administrator Privileges</h3>
                      <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                        Your account has unlimited access permissions directly managed by the system configuration. 
                        No billing subscription is required.
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-center">
                    <div className="border border-white/5 bg-white/[0.02] rounded-xl p-4">
                      <Cpu size={20} className="mx-auto mb-2 text-brand-electric" />
                      <span className="block text-[10px] text-slate-500 uppercase">API Endpoints</span>
                      <span className="block text-white font-bold text-sm mt-1">Unlimited / Dev</span>
                    </div>
                    <div className="border border-white/5 bg-white/[0.02] rounded-xl p-4">
                      <Layers size={20} className="mx-auto mb-2 text-brand-electric" />
                      <span className="block text-[10px] text-slate-500 uppercase">System Status</span>
                      <span className="block text-green-400 font-bold text-sm mt-1">Online / Stable</span>
                    </div>
                    <div className="border border-white/5 bg-white/[0.02] rounded-xl p-4">
                      <HardDrive size={20} className="mx-auto mb-2 text-brand-electric" />
                      <span className="block text-[10px] text-slate-500 uppercase">DB Clusters</span>
                      <span className="block text-white font-bold text-sm mt-1">wesitex.com:5432</span>
                    </div>
                  </div>
                </div>
              ) : isUserSubscribed ? (
                // Active Premium Member View
                <div className="space-y-6">
                  <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-5 flex gap-4 shadow-[0_0_15px_rgba(34,197,94,0.15)]">
                    <CheckCircle2 className="text-green-400 shrink-0 mt-0.5" size={24} />
                    <div>
                      <h3 className="font-bold text-white text-lg">Premium Subscription Active</h3>
                      <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                        Congratulations! Your manual subscription is fully active. You now have access to Wesi Cloud Workspace APIs and premium infrastructure services.
                      </p>
                    </div>
                  </div>

                  <div className="border border-white/10 rounded-xl p-5 bg-white/[0.01]">
                    <h4 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Sparkles size={14} className="text-brand-electric" />
                      <span>Workspace Modules Unlocked:</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-xs font-mono">
                      <button 
                        onClick={() => setActiveTab("domain")}
                        className="flex flex-col items-center p-4 rounded-xl border border-white/5 bg-white/5 hover:border-brand-purple/50 hover:bg-brand-purple/5 transition-all text-center cursor-pointer"
                      >
                        <Globe className="text-brand-electric mb-2" size={20} />
                        <span className="font-bold text-white">Domain Manager</span>
                        <span className="text-[10px] text-slate-500 mt-1">{domains.length} Domain(s)</span>
                      </button>

                      <button 
                        onClick={() => setActiveTab("hosting")}
                        className="flex flex-col items-center p-4 rounded-xl border border-white/5 bg-white/5 hover:border-brand-purple/50 hover:bg-brand-purple/5 transition-all text-center cursor-pointer"
                      >
                        <Server className="text-brand-electric mb-2" size={20} />
                        <span className="font-bold text-white">Cloud Hosting</span>
                        <span className="text-[10px] text-slate-500 mt-1">{instances.length} Server(s)</span>
                      </button>

                      <button 
                        onClick={() => setActiveTab("email")}
                        className="flex flex-col items-center p-4 rounded-xl border border-white/5 bg-white/5 hover:border-brand-purple/50 hover:bg-brand-purple/5 transition-all text-center cursor-pointer"
                      >
                        <Mail className="text-brand-electric mb-2" size={20} />
                        <span className="font-bold text-white">Email Hosting</span>
                        <span className="text-[10px] text-slate-500 mt-1">{mailboxes.length} Mailbox(es)</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Inactive Member View
                <div className="space-y-6">
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex gap-4 shadow-[0_0_15px_rgba(245,158,11,0.15)]">
                    <AlertTriangle className="text-amber-400 shrink-0 mt-0.5 animate-pulse" size={24} />
                    <div>
                      <h3 className="font-bold text-white text-lg">Subscription Pending Activation</h3>
                      <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                        Welcome to Wesitex! Your account registration is complete, but your premium subscription is currently **inactive**. 
                        Because we utilize a manual billing process, your access needs to be approved by a system administrator.
                      </p>
                    </div>
                  </div>

                  <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 font-mono text-xs text-slate-400 leading-relaxed">
                    <h4 className="font-bold text-white mb-2">How to activate your profile:</h4>
                    <p className="mb-3">
                      1. Inform the development team or administrator of your registered email: <strong>{user.email}</strong>.
                    </p>
                    <p>
                      2. The administrator will activate your subscription from the Admin Console. Once approved, refresh this page to unlock your custom domain, hosting, and email management tools!
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Support footer info */}
            <div className="mt-8 pt-5 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs font-mono text-slate-500">
              <span className="flex items-center gap-1.5">
                <HelpCircle size={14} />
                Need help? Contact <a href="mailto:wesitex.team@gmail.com" className="text-brand-electric hover:underline">wesitex.team@gmail.com</a>
              </span>
              <span>v1.0.0 Stable</span>
            </div>
          </div>

        </div>
      )}

      {/* --- Premium Tab: Domain Manager --- */}
      {activeTab === "domain" && isUserSubscribed && !isSuperadmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
          
          {/* Domain Search & Register Card */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Search & Register Domain
            </h3>
            
            <form onSubmit={handleCheckDomain} className="space-y-4">
              <div>
                <label className="block text-xs font-mono text-slate-400 mb-1.5 uppercase">Domain Prefix</label>
                <div className="flex">
                  <input
                    type="text"
                    value={domainQuery}
                    onChange={(e) => setDomainQuery(e.target.value)}
                    placeholder="mybusiness"
                    className="flex-1 bg-white/5 border border-white/10 rounded-l-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 text-white placeholder-slate-500 font-mono"
                    required
                  />
                  <select
                    value={domainTld}
                    onChange={(e) => setDomainTld(e.target.value)}
                    className="bg-slate-900 border border-y-white/10 border-r-white/10 rounded-r-xl px-2 text-xs font-mono text-brand-electric focus:outline-none cursor-pointer"
                  >
                    <option value=".com">.com</option>
                    <option value=".net">.net</option>
                    <option value=".io">.io</option>
                    <option value=".tech">.tech</option>
                    <option value=".co">.co</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isCheckingDomain}
                className="w-full bg-brand-purple hover:bg-brand-purple/80 text-white rounded-xl py-2.5 text-xs font-bold font-mono tracking-widest uppercase transition-all disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              >
                {isCheckingDomain ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Searching Domain...</span>
                  </>
                ) : (
                  <span>Check Availability</span>
                )}
              </button>
            </form>

            {/* Check Results */}
            {domainStatus !== "idle" && (
              <div className="mt-6 p-4 rounded-xl border font-mono text-xs animate-fadeIn">
                {domainStatus === "available" ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle2 size={16} />
                      <span>{domainQuery.toLowerCase()}{domainTld} is available!</span>
                    </div>
                    <button
                      onClick={handleRegisterDomain}
                      className="w-full bg-green-500/10 hover:bg-green-500/25 border border-green-500/30 text-green-400 font-bold py-2 rounded-lg text-xs uppercase tracking-wider transition-all cursor-pointer"
                    >
                      Provision Domain
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle size={16} />
                    <span>{domainQuery.toLowerCase()}{domainTld} is already registered.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Domains Directory list */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Your Registered Domains
            </h3>

            <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-950/50">
              <table className="w-full text-left border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-950 text-slate-400 font-mono text-[10px] tracking-wider uppercase">
                    <th className="px-5 py-3.5 font-bold">Domain Name</th>
                    <th className="px-5 py-3.5 font-bold">Status</th>
                    <th className="px-5 py-3.5 font-bold">Registered On</th>
                    <th className="px-5 py-3.5 font-bold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-slate-500 font-mono text-xs">
                        No domains registered yet.
                      </td>
                    </tr>
                  ) : (
                    domains.map((d, index) => (
                      <tr key={index} className="border-b border-white/5 hover:bg-white/[0.01] transition-colors">
                        <td className="px-5 py-4 font-mono text-xs text-white font-semibold">
                          {d.name}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${
                            d.status === "Active" 
                              ? "bg-green-500/10 text-green-400 border border-green-500/20" 
                              : "bg-amber-500/10 text-amber-400 border border-amber-500/20 animate-pulse"
                          }`}>
                            {d.status === "Active" ? "Active" : "Setting Up"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-xs text-slate-400 font-mono">
                          {new Date(d.created_at).toLocaleDateString("en-MY", {
                            year: "numeric",
                            month: "short",
                            day: "numeric"
                          })}
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleDeleteDomain(d.name)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-white/5 transition-all cursor-pointer"
                            title="Delete domain"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* --- Premium Tab: Cloud Hosting VM Manager --- */}
      {activeTab === "hosting" && isUserSubscribed && !isSuperadmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
          
          {/* Configure Spec Server VM */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Deploy Cloud Server
            </h3>

            <form onSubmit={handleDeployInstance} className="space-y-5">
              <div>
                <label className="block text-xs font-mono text-slate-400 mb-1.5 uppercase">Server Instance Name</label>
                <input
                  type="text"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                  placeholder="e.g. wesi-web-server"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 text-white placeholder-slate-500 font-mono"
                  required
                />
              </div>

              {/* Specs parameters */}
              <div className="space-y-4">
                {/* CPU CORES */}
                <div>
                  <div className="flex justify-between text-xs font-mono mb-1.5">
                    <span className="text-slate-400">vCPU Cores</span>
                    <span className="text-brand-electric font-bold">{cpuCores} Core(s)</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="8"
                    step="1"
                    value={cpuCores}
                    onChange={(e) => setCpuCores(parseInt(e.target.value))}
                    className="w-full accent-brand-purple bg-white/10 h-1.5 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* RAM */}
                <div>
                  <div className="flex justify-between text-xs font-mono mb-1.5">
                    <span className="text-slate-400">System Memory</span>
                    <span className="text-brand-electric font-bold">{ramSize} GB RAM</span>
                  </div>
                  <input
                    type="range"
                    min="2"
                    max="32"
                    step="2"
                    value={ramSize}
                    onChange={(e) => setRamSize(parseInt(e.target.value))}
                    className="w-full accent-brand-purple bg-white/10 h-1.5 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* SSD */}
                <div>
                  <div className="flex justify-between text-xs font-mono mb-1.5">
                    <span className="text-slate-400">SSD Storage</span>
                    <span className="text-brand-electric font-bold">{ssdSize} GB NVMe</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="200"
                    step="10"
                    value={ssdSize}
                    onChange={(e) => setSsdSize(parseInt(e.target.value))}
                    className="w-full accent-brand-purple bg-white/10 h-1.5 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-brand-purple hover:bg-brand-purple/80 text-white rounded-xl py-3 text-xs font-bold font-mono tracking-widest uppercase transition-all flex items-center justify-center gap-2 cursor-pointer mt-2 shadow-[0_0_15px_rgba(139,92,246,0.3)]"
              >
                <Plus size={14} />
                <span>Deploy Virtual Instance</span>
              </button>
            </form>
          </div>

          {/* Instances running List */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Active Server Resources
            </h3>

            <div className="space-y-4 overflow-y-auto max-h-[350px]">
              {instances.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-mono text-xs border border-white/10 rounded-xl bg-slate-950/50">
                  No active servers provisioned.
                </div>
              ) : (
                instances.map((i, index) => (
                  <div 
                    key={index}
                    className="border border-white/10 rounded-xl p-4.5 bg-slate-950/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:border-white/20"
                  >
                    <div>
                      <div className="flex items-center gap-3">
                        <Server size={18} className="text-brand-electric" />
                        <span className="font-mono font-bold text-sm text-white">{i.name}</span>
                        
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase tracking-wider ${
                          i.status === "Active" 
                            ? "bg-green-500/10 text-green-400 border border-green-500/20" 
                            : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        }`}>
                          {i.status}
                        </span>
                      </div>
                      <div className="mt-2 text-xs font-mono text-slate-400 space-y-1">
                        <p>IP Address: <strong className="text-slate-200">{i.ip}</strong></p>
                        <p>Instance Config: <span className="text-brand-electric">{i.specs}</span></p>
                      </div>
                    </div>

                    <div className="flex sm:flex-col items-end gap-3 shrink-0 self-stretch sm:justify-between sm:self-auto">
                      {i.status === "Provisioning" ? (
                        <div className="w-full sm:w-28 space-y-1">
                          <div className="flex justify-between text-[9px] font-mono text-slate-500">
                            <span>Installing OS...</span>
                            <span>{i.progress}%</span>
                          </div>
                          <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden border border-white/5">
                            <div 
                              className="bg-brand-purple h-full transition-all duration-300"
                              style={{ width: `${i.progress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleDeleteInstance(i.name)}
                          className="px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 font-mono text-[9px] uppercase tracking-wider font-bold transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                        >
                          <Trash2 size={10} />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      )}

      {/* --- Premium Tab: Email Service Mailbox Manager --- */}
      {activeTab === "email" && isUserSubscribed && !isSuperadmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
          
          {/* Create mailbox account */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Create Mailbox
            </h3>

            {domains.filter(d => d.status === "Active").length === 0 ? (
              <div className="text-center py-8 font-mono text-xs text-slate-500">
                You must have at least one <strong>Active Domain</strong> to create mailboxes. 
                Configure a domain first in Domain Manager tab.
              </div>
            ) : (
              <form onSubmit={handleCreateMailbox} className="space-y-4">
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1.5 uppercase">Mailbox Address</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={emailPrefix}
                      onChange={(e) => setEmailPrefix(e.target.value)}
                      placeholder="info"
                      className="flex-1 bg-white/5 border border-white/10 rounded-l-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 text-white placeholder-slate-500 font-mono"
                      required
                    />
                    <select
                      value={selectedDomain}
                      onChange={(e) => setSelectedDomain(e.target.value)}
                      className="bg-slate-900 border border-y-white/10 border-r-white/10 rounded-r-xl px-2 text-xs font-mono text-brand-electric focus:outline-none cursor-pointer max-w-[150px] truncate"
                    >
                      {domains
                        .filter(d => d.status === "Active")
                        .map((d, index) => (
                          <option key={index} value={d.name}>{d.name}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1.5 uppercase">Mailbox Password</label>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 text-white placeholder-slate-500 font-mono"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-purple hover:bg-brand-purple/80 text-white rounded-xl py-2.5 text-xs font-bold font-mono tracking-widest uppercase transition-all flex items-center justify-center gap-2 cursor-pointer mt-2 shadow-[0_0_15px_rgba(139,92,246,0.3)]"
                >
                  <Plus size={14} />
                  <span>Create Mailbox Account</span>
                </button>
              </form>
            )}
          </div>

          {/* Mailbox Directory Directory */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(139,92,246,0.15)] flex flex-col">
            <h3 className="font-orbitron font-bold text-sm tracking-widest text-slate-400 mb-6 uppercase border-b border-white/5 pb-3">
              Active Custom Mailboxes
            </h3>

            <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-950/50">
              <table className="w-full text-left border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-950 text-slate-400 font-mono text-[10px] tracking-wider uppercase">
                    <th className="px-5 py-3.5 font-bold">Mail Address</th>
                    <th className="px-5 py-3.5 font-bold">Created On</th>
                    <th className="px-5 py-3.5 font-bold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mailboxes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-slate-500 font-mono text-xs">
                        No mailboxes created.
                      </td>
                    </tr>
                  ) : (
                    mailboxes.map((m, index) => (
                      <tr key={index} className="border-b border-white/5 hover:bg-white/[0.01] transition-colors">
                        <td className="px-5 py-4 font-mono text-xs text-white font-semibold">
                          {m.email}
                        </td>
                        <td className="px-5 py-4 text-xs text-slate-400 font-mono">
                          {new Date(m.created_at).toLocaleDateString("en-MY", {
                            year: "numeric",
                            month: "short",
                            day: "numeric"
                          })}
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleDeleteMailbox(m.email)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-white/5 transition-all cursor-pointer"
                            title="Delete mailbox"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* --- Superadmin Console --- */}
      {activeTab === "admin" && isSuperadmin && (
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45 p-6 md:p-8 backdrop-blur-md shadow-[0_0_40px_rgba(139,92,246,0.2)] text-white animate-fadeIn">
          {/* Header */}
          <div className="border-b border-white/5 pb-4 mb-6">
            <h2 className="font-orbitron text-xl font-bold tracking-wider text-brand-electric">
              SYSTEM ADMINISTRATION CONSOLE
            </h2>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              Manage infrastructure logs, registry access, and system communication
            </p>
          </div>

          {/* Sub Tab Navigation */}
          <div className="flex gap-6 border-b border-white/5 pb-3 mb-6 text-xs font-mono">
            <button
              onClick={() => setAdminSubTab("users")}
              className={`pb-2 px-1 border-b-2 transition-all cursor-pointer ${
                adminSubTab === "users"
                  ? "border-brand-electric text-white font-bold"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              User Registry
            </button>
            <button
              onClick={() => setAdminSubTab("messages")}
              className={`pb-2 px-1 border-b-2 transition-all cursor-pointer ${
                adminSubTab === "messages"
                  ? "border-brand-electric text-white font-bold"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              Visitor Messages (NGL!)
            </button>
          </div>

          {/* Error Banner */}
          {adminError && (
            <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-xl text-sm mb-6">
              <AlertTriangle size={16} className="shrink-0" />
              <span>{adminError}</span>
            </div>
          )}

          {/* Tab 1: User Registry */}
          {adminSubTab === "users" && (
            <div className="animate-fadeIn space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="font-orbitron text-sm font-bold tracking-widest text-slate-300 uppercase">
                  User Accounts list
                </h3>
                <button
                  onClick={fetchUsers}
                  disabled={isLoadingUsers}
                  className="inline-flex items-center gap-2 text-xs font-mono bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 hover:text-brand-electric active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                >
                  <RefreshCw size={14} className={isLoadingUsers ? "animate-spin" : ""} />
                  <span>Refresh Users</span>
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users by name or email address..."
                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-slate-900 transition-all font-sans text-white placeholder-slate-500"
                />
              </div>

              {/* User Table list */}
              <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-950/70 overflow-x-auto">
                <table className="w-full text-left border-collapse font-sans text-sm min-w-[700px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-950 text-slate-400 font-mono text-[10px] tracking-wider uppercase">
                      <th className="px-5 py-3.5 font-bold">User Profile</th>
                      <th className="px-5 py-3.5 font-bold">Quiz Blueprint</th>
                      <th className="px-5 py-3.5 font-bold">Role</th>
                      <th className="px-5 py-3.5 font-bold">Registration Date</th>
                      <th className="px-5 py-3.5 font-bold">Subscription status</th>
                      <th className="px-5 py-3.5 font-bold text-center">Action Control</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingUsers && users.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500 font-mono">
                          <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-brand-purple" />
                          Syncing with Remote database...
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500 font-mono">
                          No matching user records found.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => {
                        const isUserRoleAdmin = u.role === "superadmin";
                        const isSubscribed = u.subscription_status === "active";
                        
                        return (
                          <tr
                            key={u.id}
                            className="border-b border-white/5 hover:bg-white/[0.01] transition-colors"
                          >
                            {/* Avatar & Info */}
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                {u.avatar_url ? (
                                  <img
                                    src={u.avatar_url}
                                    alt={u.name}
                                    className="w-8 h-8 rounded-full border border-white/10 shrink-0"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-brand-purple/20 border border-brand-purple/30 text-brand-electric flex items-center justify-center font-bold text-xs shrink-0 uppercase">
                                    {u.name.substring(0, 2)}
                                  </div>
                                )}
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-100">{u.name}</span>
                                  <span className="text-xs text-slate-400 font-mono">{u.email}</span>
                                </div>
                              </div>
                            </td>

                            {/* Quiz Blueprint */}
                            <td className="px-5 py-4">
                              {u.quiz_industry ? (
                                <div className="flex flex-col gap-0.5 max-w-[160px]">
                                  <span className="text-xs font-semibold text-brand-electric font-mono capitalize">
                                    💼 {u.quiz_industry}
                                  </span>
                                  <span className="text-[10px] text-slate-400 truncate block" title={u.quiz_challenges}>
                                    🎯 {u.quiz_challenges || "None"}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-500 italic font-mono">- No Quiz -</span>
                              )}
                            </td>

                            {/* Role */}
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${
                                  isUserRoleAdmin
                                    ? "bg-red-500/15 text-red-400 border border-red-500/25"
                                    : "bg-blue-500/10 text-blue-400 border border-blue-500/15"
                                }`}
                              >
                                {u.role}
                              </span>
                            </td>

                            {/* Date Joined */}
                            <td className="px-5 py-4 text-xs text-slate-400 font-mono">
                              {new Date(u.created_at).toLocaleDateString("en-MY", {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                              })}
                            </td>

                            {/* Subscription Status */}
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-2 h-2 rounded-full ${
                                    isSubscribed
                                      ? "bg-green-500 shadow-[0_0_8px_#22c55e]"
                                      : "bg-slate-500"
                                  }`}
                                />
                                <span
                                  className={`text-xs font-mono font-semibold uppercase ${
                                    isSubscribed ? "text-green-400" : "text-slate-400"
                                  }`}
                                >
                                  {isSubscribed ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4 text-center">
                              {isUserRoleAdmin ? (
                                <span className="text-xs text-slate-600 font-mono tracking-wider uppercase">
                                  [SYS ADMIN]
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleUpdateSubscription(u.id, u.subscription_status)}
                                  disabled={updatingUserId === u.id}
                                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl font-mono text-[10px] uppercase font-bold tracking-wider active:scale-95 transition-all cursor-pointer disabled:opacity-50 min-w-[130px] justify-center ${
                                    isSubscribed
                                      ? "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                                      : "bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20"
                                  }`}
                                >
                                  {updatingUserId === u.id ? (
                                    "Syncing..."
                                  ) : isSubscribed ? (
                                    <>
                                      <ShieldAlert size={12} />
                                      <span>Revoke Access</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle size={12} />
                                      <span>Approve Subs</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-white/5 text-[10px] font-mono text-slate-500 tracking-wider">
                <span>TOTAL USERS RECORDED: {users.length}</span>
                <span>WESI-PORTAL CONSOLE v1.0</span>
              </div>
            </div>
          )}

          {/* Tab 2: Visitor Messages */}
          {adminSubTab === "messages" && (
            <div className="animate-fadeIn space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="font-orbitron text-sm font-bold tracking-widest text-slate-300 uppercase">
                  Saved visitor messages
                </h3>
                <button
                  onClick={fetchMessages}
                  disabled={isLoadingMessages}
                  className="inline-flex items-center gap-2 text-xs font-mono bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 hover:text-brand-electric active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                >
                  <RefreshCw size={14} className={isLoadingMessages ? "animate-spin" : ""} />
                  <span>Refresh Messages</span>
                </button>
              </div>

              <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-950/70 overflow-x-auto">
                <table className="w-full text-left border-collapse font-sans text-sm min-w-[600px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-950 text-slate-400 font-mono text-[10px] tracking-wider uppercase">
                      <th className="px-5 py-3.5 font-bold w-1/4">Submitted Date</th>
                      <th className="px-5 py-3.5 font-bold w-3/4">Message Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingMessages && messages.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center py-12 text-slate-500 font-mono">
                          <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-brand-purple" />
                          Syncing messages...
                        </td>
                      </tr>
                    ) : messages.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center py-12 text-slate-500 font-mono text-xs">
                          No visitor messages recorded yet.
                        </td>
                      </tr>
                    ) : (
                      messages.map((m) => (
                        <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.01] transition-colors">
                          <td className="px-5 py-4 text-xs text-slate-400 font-mono">
                            {new Date(m.created_at).toLocaleString("en-MY", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </td>
                          <td className="px-5 py-4 text-slate-100 whitespace-pre-wrap font-sans">
                            {m.content}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-white/5 text-[10px] font-mono text-slate-500 tracking-wider">
                <span>TOTAL MESSAGES SAVED: {messages.length}</span>
                <span>WESI-PORTAL CONSOLE v1.0</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
